import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import HomePage from '@modules/Home/HomePage';
import UsersPage from '@modules/Users/UsersPage';
import Sidebar from '@components/sidebar';
import TopBar from '@components/topbar';
import '@styles/global.css';
import SingleHotelView from '@modules/SingleHotelView/SingleHotelViewMain';
import PayoutManagement from '@modules/PayoutManagement/PayoutManagement';
import FoodCourtApproval from '@modules/FoodCourtApproval/FoodCourtApproval';
import Dashboard from '@modules/Dashboard/Dashboard';
import LoginPage from '@modules/Login/LoginUi';
import { AuthProvider, useAuth } from 'context/login/authContext';
import LoadingScreen from '@modules/loaderScreen/LoadingScreen';
import Notifications from '@modules/notifications/Notifications';
import ForgotPassword from '@modules/Login/ForgetPassword';

// Lazy-loaded new feature pages (performance: only loaded when navigated to)
const LiveOrderMonitor = lazy(() => import('@modules/LiveOrderMonitor/LiveOrderMonitor'));
const CommissionManagement = lazy(() => import('@modules/CommissionManagement/CommissionManagement'));
const PromoManager = lazy(() => import('@modules/PromoManager/PromoManager'));
const DisputeCenter = lazy(() => import('@modules/DisputeCenter/DisputeCenter'));
const RevenueReports = lazy(() => import('@modules/RevenueReports/RevenueReports'));
const HealthScoreboard = lazy(() => import('@modules/HealthScoreboard/HealthScoreboard'));
const CustomerInsights = lazy(() => import('@modules/CustomerInsights/CustomerInsights'));
const CityHeatmap = lazy(() => import('@modules/CityHeatmap/CityHeatmap'));
const ComparisonDashboard = lazy(() => import('@modules/ComparisonDashboard/ComparisonDashboard'));
const MenuAudit = lazy(() => import('@modules/MenuAudit/MenuAudit'));
const QualityCompliance = lazy(() => import('@modules/QualityCompliance/QualityCompliance'));
const OnboardingTracker = lazy(() => import('@modules/OnboardingTracker/OnboardingTracker'));
const SLAMonitor = lazy(() => import('@modules/SLAMonitor/SLAMonitor'));
const SuspensionManager = lazy(() => import('@modules/SuspensionManager/SuspensionManager'));
const FeatureFlags = lazy(() => import('@modules/FeatureFlags/FeatureFlags'));
const PlatformSettings = lazy(() => import('@modules/PlatformSettings/PlatformSettings'));
const APIHealthMonitor = lazy(() => import('@modules/APIHealthMonitor/APIHealthMonitor'));
const BulkOperations = lazy(() => import('@modules/BulkOperations/BulkOperations'));
const RoleManager = lazy(() => import('@modules/RoleManager/RoleManager'));
const AuditLogs = lazy(() => import('@modules/AuditLogs/AuditLogs'));
const StaffManagement = lazy(() => import('@modules/StaffManagement/StaffManagement'));
const ActivityFeed = lazy(() => import('@modules/ActivityFeed/ActivityFeed'));
const NotificationCenter = lazy(() => import('@modules/NotificationCenter/NotificationCenter'));
const SupportTickets = lazy(() => import('@modules/SupportTickets/SupportTickets'));
const AppContentManager = lazy(() => import('@modules/AppContentManager/AppContentManager'));
const DeliveryZones = lazy(() => import('@modules/DeliveryZones/DeliveryZones'));
const PayoutDashboard = lazy(() => import('@modules/PayoutDashboard/PayoutDashboard'));
const RefundManager = lazy(() => import('@modules/RefundManager/RefundManager'));
const ReviewModeration = lazy(() => import('@modules/ReviewModeration/ReviewModeration'));
const RealTimeDashboard = lazy(() => import('@modules/RealTimeDashboard/RealTimeDashboard'));
const CouponAnalytics = lazy(() => import('@modules/CouponAnalytics/CouponAnalytics'));
const CustomerProfiles = lazy(() => import('@modules/CustomerProfiles/CustomerProfiles'));
const AppVersionManager = lazy(() => import('@modules/AppVersionManager/AppVersionManager'));

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Route for Login */}
          <Route path="/" element={<LoadingScreen />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forget_password" element={<ForgotPassword />} />

          {/* Protected Routes */}
          <Route path="/*" element={<ProtectedLayout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="singlehotel" element={<SingleHotelView />} />
            <Route path="foodApproval" element={<FoodCourtApproval />} />
            <Route path="foodcourts" element={<HomePage />} />
            <Route path="payout" element={<PayoutManagement />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="notifications" element={<Notifications />} />
            {/* New Feature Routes */}
            <Route path="live-orders" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><LiveOrderMonitor /></Suspense>} />
            <Route path="commissions" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><CommissionManagement /></Suspense>} />
            <Route path="promos" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><PromoManager /></Suspense>} />
            <Route path="disputes" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><DisputeCenter /></Suspense>} />
            {/* Analytics Routes */}
            <Route path="revenue-reports" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><RevenueReports /></Suspense>} />
            <Route path="health-scores" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><HealthScoreboard /></Suspense>} />
            <Route path="customer-insights" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><CustomerInsights /></Suspense>} />
            <Route path="heatmap" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><CityHeatmap /></Suspense>} />
            <Route path="comparison" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><ComparisonDashboard /></Suspense>} />
            {/* FC Management Routes */}
            <Route path="menu-audit" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><MenuAudit /></Suspense>} />
            <Route path="quality-compliance" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><QualityCompliance /></Suspense>} />
            <Route path="onboarding" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><OnboardingTracker /></Suspense>} />
            <Route path="sla-monitor" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><SLAMonitor /></Suspense>} />
            <Route path="suspension" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><SuspensionManager /></Suspense>} />
            {/* Platform Routes */}
            <Route path="feature-flags" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><FeatureFlags /></Suspense>} />
            <Route path="platform-settings" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><PlatformSettings /></Suspense>} />
            <Route path="api-health" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><APIHealthMonitor /></Suspense>} />
            <Route path="bulk-operations" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><BulkOperations /></Suspense>} />
            {/* Access Management Routes */}
            <Route path="roles" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><RoleManager /></Suspense>} />
            <Route path="audit-logs" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><AuditLogs /></Suspense>} />
            <Route path="staff" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><StaffManagement /></Suspense>} />
            <Route path="activity" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><ActivityFeed /></Suspense>} />
            {/* Cross-Platform Features */}
            <Route path="notifications" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><NotificationCenter /></Suspense>} />
            <Route path="support-tickets" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><SupportTickets /></Suspense>} />
            <Route path="app-content" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><AppContentManager /></Suspense>} />
            <Route path="delivery-zones" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><DeliveryZones /></Suspense>} />
            <Route path="payouts" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><PayoutDashboard /></Suspense>} />
            <Route path="refunds" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><RefundManager /></Suspense>} />
            <Route path="review-moderation" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><ReviewModeration /></Suspense>} />
            <Route path="live" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><RealTimeDashboard /></Suspense>} />
            <Route path="coupon-analytics" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><CouponAnalytics /></Suspense>} />
            <Route path="customers" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><CustomerProfiles /></Suspense>} />
            <Route path="app-versions" element={<Suspense fallback={<div className="page-loading">Loading...</div>}><AppVersionManager /></Suspense>} />
            {/* Catch-all redirect to dashboard if no route matches */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
};

const ProtectedLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Sidebar />
      <div className="main-content">
        <TopBar />
        <div className="content">
          <Outlet />
        </div>
      </div>
    </>
  );
};

export default AppRoutes;
